// Storefront API (public token must have unauthenticated_read_metaobjects + products)
const STOREFRONT_ENDPOINT =
  'https://my1vsg-ue.myshopify.com/api/2025-10/graphql.json';
const STOREFRONT_TOKEN = '829ba9c3e78183a7d4b309c4e771c950';
const CONNECTED_PRODUCTS_HANDLE = {
  type: 'connected_products',
  handle: 'connected-products-il737st2',
};
const COLORS_CACHE_KEY = 'connected-products-colors-v10';

const CONNECTED_PRODUCTS_QUERY = `
  query ConnectedProductColors(
    $handle: MetaobjectHandleInput!
    $productsPageSize: Int = 100
    $connectedAfter: String
  ) {
    metaobject(handle: $handle) {
      handle
      type
      connectedItems: field(key: "connected_products_items") {
        references(first: 250, after: $connectedAfter) {
          pageInfo {
            hasNextPage
            endCursor
          }
          nodes {
            ... on Metaobject {
              handle
              firstGroup: field(key: "products") {
                references(first: $productsPageSize) {
                  nodes {
                    ...ProductWithColor
                  }
                }
              }
              secondGroup: field(key: "products_2") {
                references(first: $productsPageSize) {
                  nodes {
                    ...ProductWithColor
                  }
                }
              }
              thirdGroup: field(key: "products_3") {
                references(first: $productsPageSize) {
                  nodes {
                    ...ProductWithColor
                  }
                }
              }
              fourthGroup: field(key: "products_4") {
                references(first: $productsPageSize) {
                  nodes {
                    ...ProductWithColor
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  fragment ProductWithColor on Product {
    handle
    title
    onlineStoreUrl
    featuredImage {
      url
    }
    colorMeta: metafield(namespace: "custom", key: "product_color") {
      value
      reference {
        ... on Metaobject {
          imageField: field(key: "color_in_image") {
            reference {
              ... on MediaImage {
                image {
                  url
                }
              }
            }
          }
          codeField: field(key: "color_in_code") {
            value
          }
        }
      }
    }
  }
`;

const fetchConnectedProducts = async () => {
  const cached = sessionStorage.getItem(COLORS_CACHE_KEY);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch {
      sessionStorage.removeItem(COLORS_CACHE_KEY);
    }
  }

  const allGroups = [];
  let afterCursor = null;
  let page = 1;
  let totalReferences = 0;

  // paginate the connected_items references to fetch all groups
  // eslint-disable-next-line no-constant-condition
  while (true) {
    console.debug('[colors] Fetching page', { page, afterCursor });

    const response = await fetch(STOREFRONT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query: CONNECTED_PRODUCTS_QUERY,
        variables: {
          handle: CONNECTED_PRODUCTS_HANDLE,
          productsPageSize: 100,
          connectedAfter: afterCursor,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const json = await response.json();

    // Debug: show the full response
    console.debug('[colors] GraphQL response', json);

    const metaobject = json?.data?.metaobject;
    if (!metaobject) {
      console.warn('[colors] Missing metaobject payload', { json });
      throw new Error('No metaobject payload received');
    }

    const references = metaobject.connectedItems?.references;
    if (!references) {
      console.warn('[colors] No references found in connectedItems', { metaobject });
      break;
    }

    const { nodes = [], pageInfo } = references;
    totalReferences += nodes.length;

    if (nodes.length) {
      const grouped = normalizeProducts({ connectedItems: { references: { nodes } } });
      allGroups.push(...grouped);
      console.debug('[colors] Groups added this page', {
        page,
        nodesInPage: nodes.length,
        groupsInPage: grouped.length,
        totalGroups: allGroups.length,
        note: 'Each group represents products from one metaobject field (products/products_2/products_3)',
      });
    }

    console.debug('[colors] page fetched', {
      page,
      nodes: nodes.length,
      totalReferences,
      afterCursor,
      next: pageInfo?.endCursor,
      hasNext: pageInfo?.hasNextPage,
      pageInfoExists: !!pageInfo,
    });

    if (!pageInfo || !pageInfo.hasNextPage) {
      console.debug('[colors] pagination end', {
        page,
        totalReferences,
        totalGroups: allGroups.length,
      });
      break;
    }
    afterCursor = pageInfo.endCursor;
    page += 1;

    // Safety check to prevent infinite loops
    if (page > 50) {
      console.warn('[colors] Reached maximum page limit (50), stopping pagination');
      break;
    }
  }

  console.debug('[colors] Final results', {
    totalPages: page,
    totalReferences,
    totalGroups: allGroups.length,
  });

  sessionStorage.setItem(COLORS_CACHE_KEY, JSON.stringify(allGroups));
  return allGroups;
};

const normalizeProducts = (metaobject) => {
  const metaobjects =
    metaobject?.connectedItems?.references?.nodes?.filter(Boolean) || [];

  const parsedGroups = [];

  metaobjects.forEach((metaObj) => {
    // Create separate groups for each product field
    ['firstGroup', 'secondGroup', 'thirdGroup', 'fourthGroup'].forEach((fieldKey) => {
      const references = metaObj?.[fieldKey]?.references?.nodes || [];
      if (references.length > 0) {
        const products = references.map((product) => {
          if (!product?.handle) return null;

          const colorImage =
            product?.colorMeta?.reference?.imageField?.reference?.image?.url;
          const colorCode = product?.colorMeta?.reference?.codeField?.value;
          const fallbackImage = product?.featuredImage?.url;
          const styleKey = colorImage || colorCode || fallbackImage;

          return {
            handle: product.handle,
            url: product.onlineStoreUrl || `/products/${product.handle}`,
            colorImage,
            colorCode,
            fallbackImage,
            styleKey,
          };
        }).filter(Boolean);

        if (products.length > 0) {
          parsedGroups.push(products);
        }
      }
    });
  });

  return parsedGroups;
};

const buildSwatchAnchors = (products, currentHandle) => {
  const seenStyles = new Set();
  const anchors = [];

  products.forEach((product) => {
    if (!product.styleKey || seenStyles.has(product.styleKey)) return;
    seenStyles.add(product.styleKey);

    let style = '';
    if (product.colorImage) {
      style = `background-image: url('${product.colorImage}');`;
    } else if (product.colorCode) {
      style = `background-color: ${product.colorCode};`;
    } else if (product.fallbackImage) {
      style = `background-image: url('${product.fallbackImage}');`;
    } else {
      return;
    }

    anchors.push(
      `<a href="${product.url}" class="color-swatch__item ${
        product.handle === currentHandle ? '_active' : ''
      }" style="${style}"></a>`,
    );
  });

  if (!anchors.length) return '';

  return `<div data-metaobject-colors class="product-options__section d-flex flex-wrap px-2" data-style="color">${anchors.join(
    '',
  )}</div>`;
};

const extractHandleFromUrl = (url) => {
  if (!url) return null;
  try {
    const cleanUrl = url.split('?')[0];
    const parts = cleanUrl.split('/').filter(Boolean);
    const idx = parts.indexOf('products');
    if (idx === -1 || !parts[idx + 1]) return null;
    return parts[idx + 1];
  } catch {
    return null;
  }
};

class LoadColorsJs extends HTMLElement {
  async connectedCallback() {
    const currentHandle =
      this.getAttribute('data-handle') ||
      extractHandleFromUrl(this.getAttribute('data-product')) ||
      extractHandleFromUrl(window.location.pathname);

    if (!currentHandle) {
      console.warn('[colors] No current handle resolved');
      return;
    }

    try {
      const groups = await fetchConnectedProducts();
      const normalizedHandle = currentHandle.trim();
      console.debug('[colors] groups fetched', groups.length);
      const groupIndex = groups.findIndex((group) =>
        group.some((product) => product.handle === normalizedHandle),
      );
      const groupForHandle = groupIndex >= 0 ? groups[groupIndex] : [];

      if (groupIndex === -1) {
        console.warn('[colors] No group match for handle', normalizedHandle);
        this.innerHTML = '';
        return;
      }

      console.debug('[colors] group match', {
        handle: normalizedHandle,
        groupIndex,
        groupSize: groupForHandle.length,
      });

      if (!groupForHandle.length) {
        this.innerHTML = '';
        return;
      }

      const swatchesHtml = buildSwatchAnchors(groupForHandle, currentHandle);
      if (!swatchesHtml) {
        this.innerHTML = '';
        return;
      }

      const wrapper = document.createElement('div');
      wrapper.innerHTML = `<label><span>Color:</span></label>${swatchesHtml}`;
      this.replaceWith(wrapper);
    } catch (error) {
      console.error('[colors] Failed to load', error);
      this.innerHTML = `<div style="color:red">Failed to load colors: ${error.message}</div>`;
    }
  }
}

if (!customElements.get('load-colors-js')) {
  customElements.define('load-colors-js', LoadColorsJs);
}
